import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useGymId } from '@structural-climbing/auth'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Select,
  SelectItem,
  Spinner,
} from '@structural-climbing/ui'
import { LegalStatementsModal } from './LegalStatementsModal'
import type { LegalStatement } from './LegalStatementsModal'

// `member`: every member signs it. `child`: a parent signs it once for each
// child on their account. Fixed at upload: the signatures already collected
// only make sense for the audience they were collected under.
type Audience = 'member' | 'child'

interface LegalDocument {
  id: string
  title: string
  r2_key: string
  version: number
  is_active: boolean
  is_required: boolean
  audience: Audience
  sort_order: number
  created_at: string
  /** `privacy_notice` is the gym's notice: linked while signing, never signed. */
  kind?: 'document' | 'privacy_notice'
  /** What a signer ticks, one by one, before signing this document. */
  statements?: LegalStatement[]
}

// PDF only, matching the Worker. Documents uploaded as images before this rule
// are still listed and still signed; they can only be replaced by a PDF.
const ACCEPTED = 'application/pdf,.pdf'
const MAX_BYTES = 10 * 1024 * 1024

export function LegalDocumentsSection() {
  const { getAccessTokenSilently } = useAuth()
  const gymId = useGymId()
  const { t } = useTranslation()

  const [documents, setDocuments] = useState<LegalDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [newTitle, setNewTitle] = useState('')
  const [newAudience, setNewAudience] = useState<Audience>('member')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [statementsFor, setStatementsFor] = useState<LegalDocument | null>(null)
  const [uploadingNotice, setUploadingNotice] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const updateFileInputRef = useRef<HTMLInputElement>(null)
  const noticeFileInputRef = useRef<HTMLInputElement>(null)

  async function load() {
    if (!gymId) return
    setLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${import.meta.env.VITE_API_URL as string}/legal-documents`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      // A failed read keeps what is on screen rather than emptying the list,
      // which would look like every document had been deleted.
      if (res.ok) setDocuments(((await res.json()) as LegalDocument[]) ?? [])
    } catch {
      // Same: leave the list as it was.
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymId])

  async function handleUpload(file: File) {
    setUploadError(null)
    if (!newTitle.trim()) {
      setUploadError(t('legal.errorTitleRequired'))
      return
    }
    if (file.size > MAX_BYTES) {
      setUploadError(t('legal.errorFileTooLarge'))
      return
    }
    setUploading(true)
    try {
      const token = await getAccessTokenSilently()
      const body = new FormData()
      body.append('file', file)
      body.append('title', newTitle.trim())
      body.append('audience', newAudience)
      const res = await fetch(`${import.meta.env.VITE_API_URL as string}/legal-documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      })
      const data = (await res.json()) as { success?: boolean; error?: string }
      if (data.success) {
        setNewTitle('')
        setNewAudience('member')
        await load()
      } else {
        setUploadError(data.error ?? t('legal.errorUpload'))
      }
    } catch {
      setUploadError(t('legal.errorUploadRetry'))
    } finally {
      setUploading(false)
    }
  }

  // The notice goes through the same upload as a document, marked as a notice.
  // Replacing it later is the ordinary "Update" on its row.
  async function handleUploadNotice(file: File) {
    setUploadError(null)
    if (file.size > MAX_BYTES) {
      setUploadError(t('legal.errorFileTooLarge'))
      return
    }
    setUploadingNotice(true)
    try {
      const token = await getAccessTokenSilently()
      const body = new FormData()
      body.append('file', file)
      body.append('title', t('legal.privacyNoticeTitle'))
      body.append('kind', 'privacy_notice')
      const res = await fetch(`${import.meta.env.VITE_API_URL as string}/legal-documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      })
      const data = (await res.json()) as { success?: boolean; error?: string }
      if (data.success) await load()
      else setUploadError(data.error ?? t('legal.errorUpload'))
    } catch {
      setUploadError(t('legal.errorUploadRetry'))
    } finally {
      setUploadingNotice(false)
    }
  }

  async function handleToggle(doc: LegalDocument) {
    setPendingId(doc.id)
    try {
      const token = await getAccessTokenSilently()
      await fetch(`${import.meta.env.VITE_API_URL as string}/legal-documents/${doc.id}/active`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !doc.is_active }),
      })
    } catch {
      // The reload below shows whatever the server kept.
    }
    await load()
    setPendingId(null)
  }

  async function handleUpdate(doc: LegalDocument, file: File) {
    if (file.size > MAX_BYTES) {
      setUploadError(t('legal.errorFileTooLarge'))
      return
    }
    setUpdatingId(doc.id)
    try {
      const token = await getAccessTokenSilently()
      const body = new FormData()
      body.append('file', file)
      const res = await fetch(
        `${import.meta.env.VITE_API_URL as string}/legal-documents/${doc.id}`,
        {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` },
          body,
        },
      )
      const data = (await res.json()) as { success?: boolean; error?: string }
      if (data.success) {
        await load()
      } else {
        setUploadError(data.error ?? t('legal.errorUpdate'))
      }
    } catch {
      setUploadError(t('legal.errorUpdateRetry'))
    } finally {
      setUpdatingId(null)
    }
  }

  async function handleDelete(doc: LegalDocument) {
    setUploadError(null)
    setPendingId(doc.id)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(
        `${import.meta.env.VITE_API_URL as string}/legal-documents/${doc.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      if (!res.ok) {
        // A signed document is kept as evidence. The Worker refuses, and the
        // owner needs to hear that deactivating is the way to retire it.
        const data = (await res.json().catch(() => ({}))) as { code?: string }
        setUploadError(
          t(data.code === 'DOCUMENT_SIGNED' ? 'legal.errorDeleteSigned' : 'legal.errorDelete'),
        )
        return
      }
      await load()
    } catch {
      setUploadError(t('legal.errorDelete'))
    } finally {
      setPendingId(null)
    }
  }

  // The notice is one of the gym's legal documents in storage, and its own
  // thing on screen: nobody signs it, so it never sits in the list to sign.
  const notice = documents.find((d) => d.kind === 'privacy_notice') ?? null
  const signable = documents.filter((d) => d.kind !== 'privacy_notice')

  // One row for a document or for the notice. The notice takes no statements
  // and is never deleted: signatures record which notice was linked.
  const renderRow = (doc: LegalDocument, isNotice: boolean) => (
    <div
      key={doc.id}
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-divider p-3"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{doc.title}</p>
        <p className="text-xs text-foreground/40">
          v{doc.version} · {new Date(doc.created_at).toLocaleDateString()}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {doc.audience === 'child' && !isNotice && (
          <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">
            {t('legal.childBadge')}
          </span>
        )}
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            doc.is_active ? 'bg-success/10 text-success' : 'bg-default/10 text-foreground/40'
          }`}
        >
          {doc.is_active ? t('legal.activeBadge') : t('legal.inactiveBadge')}
        </span>
        {!isNotice && (
          <Button
            size="sm"
            variant="flat"
            isDisabled={pendingId !== null || updatingId !== null}
            onClick={() => setStatementsFor(doc)}
          >
            {t('legal.statementsButton', { count: doc.statements?.length ?? 0 })}
          </Button>
        )}
        <Button
          size="sm"
          variant="flat"
          isLoading={pendingId === doc.id}
          isDisabled={pendingId !== null}
          onClick={() => void handleToggle(doc)}
        >
          {doc.is_active ? t('common.deactivate') : t('common.activate')}
        </Button>
        <Button
          size="sm"
          variant="flat"
          isLoading={updatingId === doc.id}
          isDisabled={pendingId !== null || updatingId !== null}
          onClick={() => {
            updateFileInputRef.current?.setAttribute('data-doc-id', doc.id)
            updateFileInputRef.current?.click()
          }}
        >
          {t('common.update')}
        </Button>
        {!isNotice && (
          <Button
            size="sm"
            variant="flat"
            color="danger"
            isLoading={pendingId === doc.id}
            isDisabled={pendingId !== null || updatingId !== null}
            onClick={() => void handleDelete(doc)}
          >
            {t('common.delete')}
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <Card>
      <CardHeader>
        <div>
          <h2 className="text-base font-semibold">{t('legal.title')}</h2>
          <p className="text-xs text-foreground/40">{t('legal.subtitle')}</p>
        </div>
      </CardHeader>
      <CardBody className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label={t('legal.titlePlaceholder')}
              value={newTitle}
              onValueChange={setNewTitle}
              isDisabled={uploading}
            />
            <Select
              label={t('legal.audienceLabel')}
              selectedKeys={[newAudience]}
              disallowEmptySelection
              isDisabled={uploading}
              onSelectionChange={(keys) => {
                const val = Array.from(keys)[0] as Audience | undefined
                if (val) setNewAudience(val)
              }}
            >
              <SelectItem key="member">{t('legal.audienceMember')}</SelectItem>
              <SelectItem key="child">{t('legal.audienceChild')}</SelectItem>
            </Select>
          </div>
          <Button
            size="sm"
            color="primary"
            className="self-start"
            isLoading={uploading}
            isDisabled={uploading || !newTitle.trim()}
            onClick={() => fileInputRef.current?.click()}
          >
            {t('legal.uploadDocument')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void handleUpload(file)
              e.target.value = ''
            }}
          />
          <input
            ref={updateFileInputRef}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              const docId = updateFileInputRef.current?.getAttribute('data-doc-id')
              const doc = documents.find((d) => d.id === docId)
              if (file && doc) void handleUpdate(doc, file)
              e.target.value = ''
            }}
          />
        </div>
        {uploadError && <p className="text-xs text-danger">{uploadError}</p>}

        {loading ? (
          <div className="flex justify-center py-6">
            <Spinner size="sm" color="primary" />
          </div>
        ) : signable.length === 0 ? (
          <p className="py-6 text-center text-sm text-foreground/40">{t('legal.empty')}</p>
        ) : (
          <div className="flex flex-col gap-2">{signable.map((doc) => renderRow(doc, false))}</div>
        )}

        {!loading && (
          <div className="flex flex-col gap-2 border-t border-divider pt-4">
            <div>
              <h3 className="text-sm font-semibold">{t('legal.privacyNoticeTitle')}</h3>
              <p className="text-xs text-foreground/40">{t('legal.privacyNoticeSubtitle')}</p>
            </div>
            {notice ? (
              renderRow(notice, true)
            ) : (
              <Button
                size="sm"
                variant="flat"
                className="self-start"
                isLoading={uploadingNotice}
                isDisabled={uploadingNotice}
                onClick={() => noticeFileInputRef.current?.click()}
              >
                {t('legal.privacyNoticeUpload')}
              </Button>
            )}
            <input
              ref={noticeFileInputRef}
              type="file"
              accept={ACCEPTED}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void handleUploadNotice(file)
                e.target.value = ''
              }}
            />
          </div>
        )}

        {statementsFor && (
          <LegalStatementsModal
            document={{
              id: statementsFor.id,
              title: statementsFor.title,
              statements: statementsFor.statements ?? [],
            }}
            onClose={() => setStatementsFor(null)}
            onSaved={() => void load()}
          />
        )}
      </CardBody>
    </Card>
  )
}
